///
/// EarthBrowser
///
/// Created by Matt Giger on September 19, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Email deliverability view: rolling 7-day send/bounce/complaint stats from
/// the email_send_log counters, and the browsable suppression list with
/// audited removal. Thresholds mirror the server (bounce 3%, complaint
/// 0.05%); the daily cron opens Ops tasks when they're breached.
///

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/components/ui/use-toast'
import {
  useGetEmailDeliverabilityQuery,
  useGetEmailSuppressionsQuery,
  useRemoveEmailSuppressionMutation,
  type EmailSuppression,
} from '@/redux/api/emailApi'
import { Mail, MailWarning, MailX, Search, ShieldOff, Trash2 } from 'lucide-react'
import { useState } from 'react'

const PAGE_SIZE = 50
const BOUNCE_THRESHOLD = 0.03
const COMPLAINT_THRESHOLD = 0.0005

const reasonStyles: Record<EmailSuppression['reason'], string> = {
  bounce: 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/70',
  complaint: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-500/70',
  unsubscribe: 'bg-slate-500/10 text-slate-700 dark:text-slate-400 border border-slate-500/70',
}

function ratePct(rate: number | null): string {
  if (rate === null) return '—'
  return `${(rate * 100).toFixed(2)}%`
}

function StatCard({ icon, label, value, sub, alert }: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  alert?: boolean
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div className={`p-2 rounded-lg ${alert
        ? 'bg-red-500/10 text-red-600 dark:text-red-400'
        : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-lg font-semibold leading-tight ${alert ? 'text-red-600 dark:text-red-400' : ''}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  )
}

function DeliverabilityStats() {
  const { data, isLoading } = useGetEmailDeliverabilityQuery()

  if (isLoading || !data) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
      </div>
    )
  }

  const { window, suppressionTotals } = data
  const lowVolume = window.bounceRate === null
  const suppressedTotal = Object.values(suppressionTotals).reduce((a, b) => a + b, 0)

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={<Mail className="h-4 w-4" />}
        label={`Sent (${window.days}d)`}
        value={window.sent.toLocaleString()}
        sub={`${window.blocked} blocked · ${window.failed} failed`}
      />
      <StatCard
        icon={<MailX className="h-4 w-4" />}
        label={`Bounce Rate (${window.days}d)`}
        value={ratePct(window.bounceRate)}
        sub={lowVolume ? 'Too few sends to rate' : `${window.newBounces} new · threshold 3%`}
        alert={window.bounceRate !== null && window.bounceRate > BOUNCE_THRESHOLD}
      />
      <StatCard
        icon={<MailWarning className="h-4 w-4" />}
        label={`Complaint Rate (${window.days}d)`}
        value={ratePct(window.complaintRate)}
        sub={lowVolume ? 'Too few sends to rate' : `${window.newComplaints} new · threshold 0.05%`}
        alert={window.complaintRate !== null && window.complaintRate > COMPLAINT_THRESHOLD}
      />
      <StatCard
        icon={<ShieldOff className="h-4 w-4" />}
        label="Suppressed Addresses"
        value={suppressedTotal.toLocaleString()}
        sub={['bounce', 'complaint', 'unsubscribe']
          .map(reason => `${suppressionTotals[reason] ?? 0} ${reason}`)
          .join(' · ')}
      />
    </div>
  )
}

function RemoveSuppressionDialog({ suppression, onClose }: {
  suppression: EmailSuppression
  onClose: () => void
}) {
  const [removeSuppression, { isLoading }] = useRemoveEmailSuppressionMutation()
  const [note, setNote] = useState('')

  const handleRemove = async () => {
    try {
      await removeSuppression({ email: suppression.email, note: note || undefined }).unwrap()
      toast({ title: 'Suppression removed', description: `${suppression.email} can receive email again.` })
      onClose()
    } catch {
      toast({ title: 'Removal failed', description: 'Please try again.', variant: 'destructive' })
    }
  }

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <AlertDialogContent className="rounded-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove suppression?</AlertDialogTitle>
          <AlertDialogDescription>
            {suppression.email} was suppressed for <strong>{suppression.reason}</strong>.
            Removing it allows email delivery again{suppression.reason !== 'unsubscribe'
              ? ' — make sure the underlying issue is resolved, repeat bounces or complaints hurt sender reputation'
              : ' — only do this if the user asked to be re-subscribed'}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reason for removal (recorded in the audit log)"
          aria-label="Removal note"
          className="rounded-xl"
        />
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            disabled={isLoading}
            onClick={handleRemove}
          >
            Remove Suppression
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function EmailDeliverability() {
  const [search, setSearch] = useState('')
  const [reasonFilter, setReasonFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [removing, setRemoving] = useState<EmailSuppression | null>(null)

  const { data, isLoading } = useGetEmailSuppressionsQuery({
    search: search || undefined,
    reason: reasonFilter !== 'all' ? reasonFilter : undefined,
    page,
  })

  const suppressions = data?.suppressions ?? []
  const total = data?.pagination.total ?? 0
  const pageCount = Math.max(1, data?.pagination.pages ?? 1)

  return (
    <div className="space-y-5">
      <DeliverabilityStats />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search suppressed addresses…"
            aria-label="Search suppressed addresses"
            className="pl-9 w-72 rounded-xl focus-visible:ring-2 focus-visible:ring-blue-500"
          />
        </div>
        <Select
          value={reasonFilter}
          onValueChange={(value: string) => { setReasonFilter(value); setPage(1) }}
        >
          <SelectTrigger className="w-[160px] rounded-xl focus:ring-2 focus:ring-blue-500" aria-label="Filter by reason">
            <SelectValue placeholder="Filter by reason" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all" className="rounded-lg">All Reasons</SelectItem>
            <SelectItem value="bounce" className="rounded-lg">Bounce</SelectItem>
            <SelectItem value="complaint" className="rounded-lg">Complaint</SelectItem>
            <SelectItem value="unsubscribe" className="rounded-lg">Unsubscribe</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total.toLocaleString()} suppressed</span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      ) : suppressions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {search || reasonFilter !== 'all'
              ? 'No suppressions match the current filters.'
              : 'The suppression list is empty.'}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Added</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {suppressions.map(suppression => (
                <TableRow key={suppression.email}>
                  <TableCell className="font-mono text-xs">{suppression.email}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-full capitalize ${reasonStyles[suppression.reason]}`}>
                      {suppression.reason}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(suppression.added_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {suppression.expires_at ? new Date(suppression.expires_at).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove suppression for ${suppression.email}`}
                      title="Remove suppression"
                      className="hover:bg-red-500/10"
                      onClick={() => setRemoving(suppression)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
            >
              Previous
            </Button>
            <span className="text-sm px-2">Page {page} of {pageCount}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= pageCount}
              className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {removing && (
        <RemoveSuppressionDialog suppression={removing} onClose={() => setRemoving(null)} />
      )}
    </div>
  )
}
